import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('GoogleBusinessProfileService', 'gbp');

// Mirrors src/components/encompass/prospects/pricing-v2/hooks/useGroupedPricingSave.ts:159
// supabase.functions.invoke leaves data: null on non-2xx and stashes the body on
// either the response field or error.context (a Response). Read from both.
async function readInvokeErrorBody(
  error: unknown,
  response: Response | undefined
): Promise<Record<string, unknown> | null> {
  const resp =
    response ??
    ((error as { context?: unknown } | null)?.context instanceof Response
      ? ((error as { context: Response }).context)
      : undefined);
  if (!(resp instanceof Response)) {
return null;
}
  try {
    return (await resp.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// Rethrow as a typed Error whose message is the server's error code (e.g.
// 'forbidden', 'oauth_not_configured', 'connect_in_progress'). Falls back to
// the underlying error message when no body is parseable.
async function rethrowWithServerCode(
  fnName: string,
  error: unknown,
  response: Response | undefined
): Promise<never> {
  const body = await readInvokeErrorBody(error, response);
  const code = typeof body?.error === 'string' ? body.error : (error as Error).message;
  log.error(`${fnName} failed`, { error, code }, error as Error);
  throw new Error(code);
}

export type GbpStatus = 'pending_location' | 'active' | 'disconnected';

export interface GbpConnectionStatus {
  hasConnection: boolean;
  status: GbpStatus | null;
  accountDisplayName: string | null;
  locationDisplayName: string | null;
  connectedAt: string | null;
  lastValidatedAt: string | null;
  lastRefreshError: string | null;
  needsReconnect: boolean;
}

export interface AccountLocationPair {
  account: { resource_name: string; display_name: string };
  location: { resource_name: string; display_name: string };
}

export interface ListLocationsResult {
  awaitingApproval: boolean;
  pairs?: AccountLocationPair[];
  message?: string;
}

class GoogleBusinessProfileService {
  async getStatus(): Promise<GbpConnectionStatus> {
    const { data, error } = await supabase.rpc('check_gbp_connection_status');
    if (error) {
      log.error('check_gbp_connection_status failed', { error }, error as Error);
      throw error;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || row.has_connection === false) {
      return {
        hasConnection: false,
        status: null,
        accountDisplayName: null,
        locationDisplayName: null,
        connectedAt: null,
        lastValidatedAt: null,
        lastRefreshError: null,
        needsReconnect: false
      };
    }
    return {
      hasConnection: true,
      status: row.status as GbpStatus,
      accountDisplayName: row.account_display_name ?? null,
      locationDisplayName: row.location_display_name ?? null,
      connectedAt: row.connected_at ?? null,
      lastValidatedAt: row.last_validated_at ?? null,
      lastRefreshError: row.last_refresh_error ?? null,
      needsReconnect: row.needs_reconnect === true
    };
  }

  /**
   * Kicks off the OAuth flow by asking the edge function for the Google consent URL.
   * Caller is responsible for redirecting the browser. On non-2xx, throws an Error
   * whose message is the server's error code (e.g., 'oauth_not_configured',
   * 'connect_in_progress') so the UI can branch on it.
   */
  async startConnect(): Promise<{ url: string }> {
    const { data, error, response } = await supabase.functions.invoke<{ url: string }>(
      'gbp-oauth-start',
      { method: 'POST' }
    );
    if (error) {
      await rethrowWithServerCode('gbp-oauth-start', error, response);
    }
    if (!data?.url) {
      throw new Error('gbp-oauth-start returned no URL');
    }
    return { url: data.url };
  }

  async listLocations(): Promise<ListLocationsResult> {
    const { data, error, response } = await supabase.functions.invoke<ListLocationsResult>(
      'gbp-list-locations',
      { method: 'POST' }
    );
    if (error) {
      await rethrowWithServerCode('gbp-list-locations', error, response);
    }
    return data ?? { awaitingApproval: false, pairs: [] };
  }

  async finalizeLocation(payload: {
    account_resource_name: string;
    location_resource_name: string;
  }): Promise<void> {
    const { error, response } = await supabase.functions.invoke('gbp-finalize-location', {
      method: 'POST',
      body: payload
    });
    if (error) {
      await rethrowWithServerCode('gbp-finalize-location', error, response);
    }
  }

  async disconnect(): Promise<void> {
    const { error, response } = await supabase.functions.invoke('gbp-disconnect', {
      method: 'POST'
    });
    if (error) {
      await rethrowWithServerCode('gbp-disconnect', error, response);
    }
  }

  async verify(): Promise<{ verified: boolean; reconnectRequired: boolean; message?: string }> {
    const { data, error, response } = await supabase.functions.invoke<{
      ok: boolean;
      verified: boolean;
      reconnectRequired?: boolean;
      message?: string;
    }>('gbp-verify-connection', { method: 'POST' });
    if (error) {
      await rethrowWithServerCode('gbp-verify-connection', error, response);
    }
    return {
      verified: data?.verified === true,
      reconnectRequired: data?.reconnectRequired === true,
      message: data?.message
    };
  }
}

export const googleBusinessProfileService = new GoogleBusinessProfileService();
